import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Briefcase, FileText, CheckCircle, Clock, AlertTriangle, 
  DollarSign, Users, TrendingUp, Plus, Eye, Check, X,
  Download, Send, Receipt, Truck, ClipboardCheck, Search,
  Upload, Image, File, Trash2, Wrench, MessageSquare, Paperclip,
  Zap, Timer, AlertOctagon, UserPlus, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Priority SLA definitions
const PRIORITY_SLA = {
  'Emergency': { label: 'Emergency', sla: '4 hours', hours: 4, color: 'bg-red-600 text-white animate-pulse' },
  'Urgent': { label: 'Urgent', sla: '24 hours', hours: 24, color: 'bg-red-500 text-white' },
  'High': { label: 'High', sla: '48 hours', hours: 48, color: 'bg-orange-500 text-white' },
  'Medium': { label: 'Medium', sla: '5 days', hours: 120, color: 'bg-blue-500 text-white' },
  'Low': { label: 'Low', sla: '30 days', hours: 720, color: 'bg-gray-500 text-white' },
};

// Status colors
// Wyldecrest Stabilised Workflow - 8 Stages Only
const STATUS_COLORS = {
  'Job Created': 'bg-blue-100 text-blue-700',
  'Awaiting Quote': 'bg-purple-100 text-purple-700',
  'Quote Awaiting Approval': 'bg-amber-100 text-amber-700',
  'Quote Requested': 'bg-purple-100 text-purple-700',
  'Quote Received': 'bg-amber-100 text-amber-700',
  'Approved': 'bg-green-100 text-green-700',
  'Assigned': 'bg-cyan-100 text-cyan-700',
  'Scheduled': 'bg-teal-100 text-teal-700',
  'Completed': 'bg-emerald-100 text-emerald-700',
  'Closed': 'bg-slate-100 text-slate-600',
  'Manual Intervention Required': 'bg-red-100 text-red-700 animate-pulse',
};

const PRIORITY_COLORS = {
  'Low': 'bg-gray-100 text-gray-600',
  'Medium': 'bg-blue-100 text-blue-700',
  'High': 'bg-orange-100 text-orange-700',
  'Urgent': 'bg-red-100 text-red-700',
  'Emergency': 'bg-red-200 text-red-800 animate-pulse',
};

// Next Action - now returns from backend enriched data or fallback
const getNextAction = (job) => {
  // If backend provided next_action, use it
  if (job.next_action) {
    const isEscalated = job.next_action.includes('ESCALATED') || job.next_action.includes('Manual');
    const isWaiting = job.next_action.includes('Waiting') || job.next_action.includes('Awaiting');
    return { 
      text: job.next_action, 
      color: isEscalated ? 'text-red-600 font-semibold' : isWaiting ? 'text-blue-600' : 'text-amber-600'
    };
  }
  
  // Fallback based on status
  const status = job.status || job.stage;
  switch (status) {
    case 'Job Created':
      return { text: 'Assign Contractor', color: 'text-blue-600' };
    case 'Awaiting Quote':
    case 'Quote Requested':
      return { text: 'Request Quote', color: 'text-purple-600' };
    case 'Quote Awaiting Approval':
    case 'Quote Received':
      return { text: 'Review Quote', color: 'text-amber-600' };
    case 'Approved':
      return { text: 'Issue PO', color: 'text-green-600' };
    case 'Assigned':
      return { text: 'Schedule Visit', color: 'text-cyan-600' };
    case 'Scheduled':
      return { text: 'Awaiting Visit', color: 'text-teal-600' };
    case 'Completed':
      return { text: 'Review & Close', color: 'text-emerald-600' };
    case 'Closed':
      return { text: 'Done', color: 'text-slate-500' };
    case 'Manual Intervention Required':
      return { text: '⚠️ ESCALATED', color: 'text-red-600 font-bold' };
    default:
      return { text: 'View Details', color: 'text-slate-500' };
  }
};

export default function FMDashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobQuotes, setJobQuotes] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null); // For status filtering
  const [categoryFilter, setCategoryFilter] = useState(null); // For category filtering from PPM
  const [priorityFilter, setPriorityFilter] = useState(null); // For priority filtering
  const [searchTerm, setSearchTerm] = useState(''); // Live search
  const [parks, setParks] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [attentionJobs, setAttentionJobs] = useState([]); // Jobs needing immediate attention
  const token = localStorage.getItem('token');

  const headers = { Authorization: `Bearer ${token}` };

  const loadDashboard = useCallback(async () => {
    try {
      const [dashRes, statusRes, parksRes, contractorsRes] = await Promise.all([
        axios.get(`${API}/api/fm/dashboard`, { headers }),
        axios.get(`${API}/api/fm/status`),
        axios.get(`${API}/api/parks`, { headers }),
        axios.get(`${API}/api/contractors`, { headers }),
      ]);
      setDashboard(dashRes.data);
      setSystemStatus(statusRes.data);
      setParks(parksRes.data || []);
      setContractors(contractorsRes.data || []);
    } catch (err) {
      console.error('Dashboard load error:', err);
    }
  }, []);
  
  // Load jobs requiring immediate attention (escalated, overdue, manual intervention)
  const loadAttentionJobs = useCallback(async (jobsList) => {
    try {
      // Filter jobs that need attention
      const attentionStatuses = ['Manual Intervention Required', 'Escalated'];
      const needsAttention = jobsList.filter(job => {
        // Check if escalated
        if (attentionStatuses.some(s => (job.status || '').includes(s))) return true;
        // Check if overdue (SLA deadline passed)
        if (job.sla_deadline && new Date(job.sla_deadline) < new Date()) return true;
        // Check if reassign count > 0
        if (job.reassign_count > 0) return true;
        return false;
      });
      
      if (needsAttention.length > 0) {
        // Fetch next actions for these jobs from backend
        const jobIds = needsAttention.map(j => j.id || j.internal_id).filter(Boolean);
        try {
          const res = await axios.post(`${API}/api/control/batch-next-actions`, { job_ids: jobIds });
          const actionsMap = {};
          (res.data.actions || []).forEach(a => {
            actionsMap[a.job_id] = a.next_action;
          });
          // Enrich jobs with next actions
          const enriched = needsAttention.map(job => ({
            ...job,
            next_action: actionsMap[job.id] || actionsMap[job.internal_id] || job.next_action
          }));
          setAttentionJobs(enriched);
        } catch {
          setAttentionJobs(needsAttention);
        }
      } else {
        setAttentionJobs([]);
      }
    } catch (err) {
      console.error('Attention jobs load error:', err);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '500');
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      
      const res = await axios.get(`${API}/api/fm/jobs?${params.toString()}`, { headers });
      const jobsList = res.data.jobs || [];
      setJobs(jobsList);
      loadAttentionJobs(jobsList);
      return jobsList;
    } catch (err) {
      console.error('Jobs load error:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, searchTerm, loadAttentionJobs]);
  
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadJobs();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Auto-select job from URL parameter or filter by category
  useEffect(() => {
    const jobId = searchParams.get('job');
    const category = searchParams.get('category');
    
    if (category) {
      setCategoryFilter(category);
      setActiveTab('pipeline');
      // Clear the search param after setting filter
      setSearchParams({});
    }
    
    if (jobId && jobs.length > 0) {
      const job = jobs.find(j => j.id === jobId);
      if (job) {
        setSelectedJob(job);
        setActiveTab('pipeline');
        // Clear the search param after selecting
        setSearchParams({});
      }
    }
  }, [searchParams, jobs, setSearchParams]);

  useEffect(() => {
    loadDashboard();
    loadJobs();
  }, [loadDashboard, loadJobs]);

  const loadJobQuotes = async (jobId) => {
    try {
      const res = await axios.get(`${API}/api/fm/jobs/${jobId}/quotes`, { headers });
      setJobQuotes(res.data.quotes || []);
    } catch (err) {
      toast.error('Failed to load quotes');
    }
  };

  const approveQuote = async (quoteId) => {
    try {
      const res = await axios.post(`${API}/api/fm/quotes/${quoteId}/approve`, {}, { headers });
      toast.success(`PO Generated: ${res.data.po_number}`);
      loadJobs();
      if (selectedJob) {
        loadJobQuotes(selectedJob.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve quote');
    }
  };

  const rejectQuote = async (quoteId) => {
    try {
      await axios.post(`${API}/api/fm/quotes/${quoteId}/reject`, {}, { headers });
      toast.success('Quote rejected');
      if (selectedJob) {
        loadJobQuotes(selectedJob.id);
      }
    } catch (err) {
      toast.error('Failed to reject quote');
    }
  };

  const downloadReport = async () => {
    try {
      window.open(`${API}/api/fm/reports/weekly-payment?format=csv`, '_blank');
      toast.success('Report downloading...');
    } catch (err) {
      toast.error('Failed to download report');
    }
  };

  // Handle KPI card click to filter jobs
  const handleKPIClick = (filterType) => {
    setStatusFilter(filterType);
    setCategoryFilter(null); // Clear category filter when using status filter
    setPriorityFilter(null); // Clear priority filter
    setSelectedJob(null);
  };

  // Handle Priority card click
  const handlePriorityClick = (priority) => {
    setPriorityFilter(priority);
    setStatusFilter(null);
    setCategoryFilter(null);
    setSelectedJob(null);
  };

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter(null);
    setCategoryFilter(null);
    setPriorityFilter(null);
  };

  // Get filtered jobs based on statusFilter and categoryFilter
  const getFilteredJobs = () => {
    let result = jobs;
    
    // Apply category filter first (from PPM navigation)
    if (categoryFilter) {
      // Match jobs whose job_type_label or description contains the category
      const categoryLower = categoryFilter.toLowerCase();
      result = jobs.filter(j => {
        const typeLabel = (j.job_type_label || '').toLowerCase();
        const jobType = (j.job_type || '').toLowerCase();
        const desc = (j.description || '').toLowerCase();
        
        // Map PPM categories to FM job types
        const categoryMappings = {
          'compliance': ['compliance', 'eicr', 'gas', 'lpg', 'legionella', 'fire'],
          'electrical': ['electrical', 'eicr', 'visual_electrical'],
          'gas': ['gas', 'lpg', 'boiler'],
          'water': ['water', 'legionella', 'drain'],
          'drainage': ['drain', 'drainage'],
          'hvac': ['hvac', 'ac', 'air conditioning', 'heating'],
          'structural': ['structural', 'roof', 'fabric', 'seams'],
          'services': ['service', 'boiler', 'maintenance'],
          'seasonal': ['seasonal', 'drain_down', 'reconnect', 'winter', 'spring'],
          'grounds': ['grounds', 'garden', 'external'],
          'fabric': ['fabric', 'seams', 'seals', 'exterior'],
          'fire & safety': ['fire', 'safety', 'extinguisher', 'emergency', 'lighting']
        };
        
        const matchTerms = categoryMappings[categoryLower] || [categoryLower];
        return matchTerms.some(term => 
          typeLabel.includes(term) || 
          jobType.includes(term) || 
          desc.includes(term)
        );
      });
    }
    
    // Then apply status filter if set
    if (statusFilter) {
      switch (statusFilter) {
        case 'all':
          break; // No additional filtering
        case 'pending_quotes':
          result = result.filter(j => ['Quote Requested', 'Quotes Received'].includes(j.status));
          break;
        case 'awaiting_approval':
          result = result.filter(j => j.status === 'Awaiting Approval');
          break;
        case 'po_issued':
          result = result.filter(j => j.status === 'PO Issued');
          break;
        case 'in_progress':
          result = result.filter(j => j.status === 'In Progress');
          break;
        case 'pending_invoices':
          result = result.filter(j => ['Awaiting Invoice', 'Invoice Review'].includes(j.status));
          break;
        default:
          break;
      }
    }
    
    return result;
  };

  const filteredJobs = (() => {
    let result = getFilteredJobs();
    
    // Apply priority filter
    if (priorityFilter) {
      result = result.filter(j => j.priority === priorityFilter);
    }
    
    return result;
  })();

  // Create Job Form Component with Park Search, Contractor Assignment, Notes, Attachments
  const CreateJobForm = () => {
    const [formData, setFormData] = useState({
      site_name: '',
      postcode: '',
      job_type: '',
      priority: 'Medium',
      description: '',
      client_name: '',
      client_email: '',
      plot_number: '',
      assigned_contractor_id: '',
      notes: '',
    });
    const [parkSearch, setParkSearch] = useState('');
    const [parkOpen, setParkOpen] = useState(false);
    const [contractorSearch, setContractorSearch] = useState('');
    const [contractorOpen, setContractorOpen] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);

    const parkSearchLower = parkSearch.toLowerCase();
    const filteredParks = parks.filter(p => 
      (p.name || '').toLowerCase().includes(parkSearchLower) ||
      (p.postcode || '').toLowerCase().includes(parkSearchLower)
    ).slice(0, 10);

    const contractorSearchLower = contractorSearch.toLowerCase();
    const filteredContractors = contractors.filter(c => 
      (c.company_name || c.name || '').toLowerCase().includes(contractorSearchLower) ||
      (c.specialties || []).some(s => s.toLowerCase().includes(contractorSearchLower))
    ).slice(0, 10);

    const selectedContractor = contractors.find(c => c.id === formData.assigned_contractor_id);

    const handleParkSelect = (park) => {
      setFormData({
        ...formData, 
        site_name: park.name, 
        postcode: park.postcode || ''
      });
      setParkOpen(false);
      setParkSearch('');
    };

    const handleContractorSelect = (contractor) => {
      setFormData({...formData, assigned_contractor_id: contractor.id});
      setContractorOpen(false);
      setContractorSearch('');
    };

    const handleFileUpload = async (e, type) => {
      const files = Array.from(e.target.files);
      const maxImages = 50;
      const maxDocs = 6;
      
      const currentImages = attachments.filter(a => a.type === 'image').length;
      const currentDocs = attachments.filter(a => a.type === 'document').length;
      
      for (const file of files) {
        const isImage = file.type.startsWith('image/');
        
        if (isImage && currentImages >= maxImages) {
          toast.error(`Maximum ${maxImages} images allowed`);
          continue;
        }
        if (!isImage && currentDocs >= maxDocs) {
          toast.error(`Maximum ${maxDocs} documents allowed`);
          continue;
        }
        
        // Create preview for images
        const attachment = {
          id: `temp_${Date.now()}_${Math.random()}`,
          name: file.name,
          size: file.size,
          type: isImage ? 'image' : 'document',
          file: file,
          preview: isImage ? URL.createObjectURL(file) : null,
        };
        
        setAttachments(prev => [...prev, attachment]);
      }
    };

    const removeAttachment = (id) => {
      setAttachments(prev => prev.filter(a => a.id !== id));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setUploading(true);
      try {
        // Calculate SLA hours based on priority
        const slaHoursMap = {
          'Emergency': 4,
          'Urgent': 24,
          'High': 48,
          'Medium': 120,
          'Low': 720
        };
        
        // First, create the job
        const jobPayload = {
          site_name: formData.site_name,
          postcode: formData.postcode,
          job_type: formData.job_type,
          priority: formData.priority,
          description: formData.description,
          client_name: formData.client_name || null,
          client_email: formData.client_email || null,
          plot_number: formData.plot_number || null,
          notes: formData.notes || null,
          assigned_contractor_id: formData.assigned_contractor_id || null,
          sla_hours: slaHoursMap[formData.priority] || 120,
        };
        
        const res = await axios.post(`${API}/api/fm/jobs`, jobPayload, { headers });
        const createdJob = res.data.job;
        
        // Then upload attachments if any
        if (attachments.length > 0) {
          let uploadedCount = 0;
          for (const att of attachments) {
            if (att.file) {
              const fileFormData = new FormData();
              fileFormData.append('file', att.file);
              fileFormData.append('file_category', att.type);
              
              try {
                await axios.post(
                  `${API}/api/fm/jobs/${createdJob.id}/attachments`,
                  fileFormData,
                  { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
                );
                uploadedCount++;
              } catch (uploadErr) {
                console.error(`Failed to upload ${att.name}:`, uploadErr);
              }
            }
          }
          if (uploadedCount > 0) {
            toast.success(`Job created with ${uploadedCount} attachment(s): ${createdJob.job_reference}`);
          } else {
            toast.success(`Job created: ${createdJob.job_reference}`);
          }
        } else {
          toast.success(`Job created: ${createdJob.job_reference}`);
        }
        
        setShowCreateJob(false);
        loadJobs();
        loadDashboard();
      } catch (err) {
        const detail = err.response?.data?.detail;
        const errMsg = typeof detail === 'string' ? detail : 
                       Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(', ') : 
                       'Failed to create job';
        toast.error(errMsg);
      } finally {
        setUploading(false);
      }
    };

    const imageCount = attachments.filter(a => a.type === 'image').length;
    const docCount = attachments.filter(a => a.type === 'document').length;

    return (
      <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2" data-testid="create-job-form">
        {/* Site Selection */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Site/Park Name *</Label>
            <Popover open={parkOpen} onOpenChange={setParkOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between font-normal"
                  data-testid="park-search-btn"
                >
                  {formData.site_name || "Search parks..."}
                  <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput 
                    placeholder="Search parks..." 
                    value={parkSearch}
                    onValueChange={setParkSearch}
                    data-testid="park-search-input"
                  />
                  <CommandList>
                    <CommandEmpty>No parks found.</CommandEmpty>
                    <CommandGroup>
                      {filteredParks.map(park => (
                        <CommandItem
                          key={park.id}
                          value={park.id}
                          onSelect={() => handleParkSelect(park)}
                          className="cursor-pointer"
                          data-testid={`park-option-${park.id}`}
                        >
                          <div className="flex-1">
                            <div className="font-medium">{park.name}</div>
                            <div className="text-xs text-slate-500">{park.postcode} - {park.region}</div>
                          </div>
                          {formData.site_name === park.name && <Check className="h-4 w-4" />}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label>Postcode *</Label>
            <Input
              value={formData.postcode}
              onChange={e => setFormData({...formData, postcode: e.target.value.toUpperCase()})}
              placeholder="Auto-populated or enter manually"
              required
              data-testid="job-postcode-input"
            />
          </div>
        </div>

        {/* Job Type & Priority with SLA */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Job Type *</Label>
            <Select value={formData.job_type} onValueChange={v => setFormData({...formData, job_type: v})}>
              <SelectTrigger data-testid="job-type-select">
                <SelectValue placeholder="Select job type" />
              </SelectTrigger>
              <SelectContent>
                {systemStatus?.job_types?.map(type => (
                  <SelectItem key={type} value={type}>
                    {systemStatus.job_type_labels?.[type] || type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority (SLA) *</Label>
            <Select value={formData.priority} onValueChange={v => setFormData({...formData, priority: v})}>
              <SelectTrigger data-testid="job-priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PRIORITY_SLA).map(([key, val]) => (
                  <SelectItem key={key} value={key} data-testid={`priority-option-${key.toLowerCase()}`}>
                    <div className="flex items-center gap-2">
                      {key === 'Emergency' && <Zap className="h-4 w-4 text-red-600" />}
                      {key === 'Urgent' && <AlertOctagon className="h-4 w-4 text-red-500" />}
                      {key === 'High' && <Timer className="h-4 w-4 text-orange-500" />}
                      <span>{key}</span>
                      <span className="text-xs text-slate-500">({val.sla})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.priority && (
              <p className="text-xs text-slate-500 mt-1" data-testid="sla-display">
                SLA: {PRIORITY_SLA[formData.priority]?.sla}
              </p>
            )}
          </div>
        </div>

        {/* Description */}
        <div>
          <Label>Description *</Label>
          <Textarea
            value={formData.description}
            onChange={e => setFormData({...formData, description: e.target.value})}
            placeholder="Describe the work required..."
            rows={3}
            required
            data-testid="job-description-input"
          />
        </div>

        {/* Contractor Assignment - Teal Theme */}
        <div className="p-4 bg-[#1F7A78]/10 rounded-lg border-2 border-[#1F7A78]/30">
          <Label className="flex items-center gap-2 mb-2 text-[#1F7A78] font-semibold">
            <UserPlus className="h-4 w-4" />
            Assign Contractor
          </Label>
          <Popover open={contractorOpen} onOpenChange={setContractorOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                className="w-full justify-between font-normal border-[#1F7A78]/50 hover:bg-[#1F7A78]/5"
                data-testid="contractor-search-btn"
              >
                {selectedContractor ? (
                  <span className="font-medium">{selectedContractor.company_name || selectedContractor.name}</span>
                ) : (
                  <span className="text-slate-500">Click to assign contractor...</span>
                )}
                <Search className="ml-2 h-4 w-4 shrink-0 text-[#1F7A78]" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput 
                  placeholder="Search by name or specialty..." 
                  data-testid="contractor-search-input" 
                  value={contractorSearch}
                  onValueChange={setContractorSearch}
                />
                <CommandList>
                  <CommandEmpty>No contractors found.</CommandEmpty>
                  <CommandGroup>
                    {filteredContractors.map(contractor => (
                      <CommandItem
                        key={contractor.id}
                        value={contractor.id}
                        onSelect={() => handleContractorSelect(contractor)}
                        className="cursor-pointer"
                      >
                        <div className="flex-1">
                          <div className="font-medium">{contractor.company_name || contractor.name}</div>
                          <div className="text-xs text-slate-500">
                            {(contractor.specialties || []).slice(0,3).join(', ')}
                          </div>
                        </div>
                        {formData.assigned_contractor_id === contractor.id && <Check className="h-4 w-4 text-[#1F7A78]" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {selectedContractor && (
            <div className="mt-2 flex items-center justify-between text-sm bg-white p-2 rounded border">
              <div>
                <span className="font-medium text-[#1F7A78]">{selectedContractor.company_name || selectedContractor.name}</span>
                <span className="text-slate-500 ml-2">{selectedContractor.phone || selectedContractor.email}</span>
              </div>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm"
                className="text-red-500 hover:text-red-700"
                onClick={() => setFormData({...formData, assigned_contractor_id: ''})}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Client Info */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div>
            <Label>Client Name</Label>
            <Input
              value={formData.client_name}
              onChange={e => setFormData({...formData, client_name: e.target.value})}
              placeholder="Contact name"
            />
          </div>
          <div>
            <Label>Client Email</Label>
            <Input
              type="email"
              value={formData.client_email}
              onChange={e => setFormData({...formData, client_email: e.target.value})}
              placeholder="email@example.com"
            />
          </div>
          <div>
            <Label>Plot Number</Label>
            <Input
              value={formData.plot_number}
              onChange={e => setFormData({...formData, plot_number: e.target.value})}
              placeholder="e.g., 42"
            />
          </div>
        </div>

        {/* Notes Section - For Communication */}
        <div className="p-4 bg-[#1F7A78]/5 rounded-lg border-2 border-[#1F7A78]/20">
          <Label className="flex items-center gap-2 mb-2 text-[#1F7A78] font-semibold">
            <MessageSquare className="h-4 w-4" />
            Notes / Instructions for Contractor
          </Label>
          <Textarea
            value={formData.notes}
            onChange={e => setFormData({...formData, notes: e.target.value})}
            placeholder="Add notes, special instructions, or communication for the contractor..."
            rows={3}
            className="bg-white border-[#1F7A78]/30"
            data-testid="job-notes-input"
          />
          <p className="text-xs text-slate-500 mt-1">These notes will be visible to the assigned contractor</p>
        </div>

        {/* Attachments Section */}
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200" data-testid="attachments-section">
          <Label className="flex items-center gap-2 mb-3">
            <Paperclip className="h-4 w-4 text-amber-600" />
            Attachments
            <span className="text-xs text-slate-500 ml-2" data-testid="attachment-counts">
              Images: {imageCount}/50 | Documents: {docCount}/6
            </span>
          </Label>
          
          <div className="flex gap-2 mb-3">
            <label className="cursor-pointer">
              <Input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleFileUpload(e, 'image')}
                data-testid="image-upload-input"
              />
              <Button type="button" variant="outline" size="sm" asChild data-testid="add-images-btn">
                <span><Image className="h-4 w-4 mr-2" />Add Images</span>
              </Button>
            </label>
            <label className="cursor-pointer">
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                multiple
                className="hidden"
                onChange={(e) => handleFileUpload(e, 'document')}
                data-testid="document-upload-input"
              />
              <Button type="button" variant="outline" size="sm" asChild data-testid="add-documents-btn">
                <span><File className="h-4 w-4 mr-2" />Add Documents</span>
              </Button>
            </label>
          </div>

          {/* Attachment Previews */}
          {attachments.length > 0 && (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="attachment-previews">
              {attachments.map(att => (
                <div key={att.id} className="relative group">
                  {att.type === 'image' ? (
                    <img 
                      src={att.preview} 
                      alt={att.name}
                      className="w-full h-16 object-cover rounded border"
                    />
                  ) : (
                    <div className="w-full h-16 bg-white rounded border flex items-center justify-center">
                      <File className="h-6 w-6 text-slate-400" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeAttachment(att.id)}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-attachment-${att.id}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <p className="text-xs truncate mt-1">{att.name}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={() => setShowCreateJob(false)} data-testid="create-job-cancel-btn">
            Cancel
          </Button>
          <Button 
            type="submit" 
            className="bg-[#1F7A78] hover:bg-[#16615f]"
            disabled={uploading}
            data-testid="create-job-submit-btn"
          >
            {uploading ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Creating...
              </>
            ) : (
              'Create Job'
            )}
          </Button>
        </DialogFooter>
      </form>
    );
  };

  // Job Detail Panel
  const JobDetailPanel = ({ job }) => {
    useEffect(() => {
      if (job) {
        loadJobQuotes(job.id);
      }
    }, [job]);

    if (!job) return null;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold">{job.job_reference}</h3>
            <p className="text-sm text-slate-500">{job.site_name}{job.plot_number ? ` • Plot ${job.plot_number}` : ''} • {job.postcode}</p>
          </div>
          <div className="flex gap-2">
            <Badge className={STATUS_COLORS[job.status]}>{job.status}</Badge>
            <Badge className={PRIORITY_COLORS[job.priority]}>{job.priority}</Badge>
          </div>
        </div>

        {/* View Full Details Button */}
        <Button 
          className="w-full bg-[#1F7A78] hover:bg-[#16615f]"
          onClick={() => navigate(`/fm/jobs/${job.id}`)}
          data-testid="view-full-job-btn"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          View Full Job Details
        </Button>

        {job.assigned_contractor_name && (
          <div className="bg-teal-50 p-3 rounded-lg border border-teal-200">
            <p className="text-xs text-slate-500">Assigned Contractor</p>
            <p className="font-semibold text-[#1F7A78]">{job.assigned_contractor_name}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Job Type:</span>
            <span className="ml-2 font-medium">{job.job_type_label}</span>
          </div>
          <div>
            <span className="text-slate-500">Postcode:</span>
            <span className="ml-2 font-medium">{job.postcode}</span>
          </div>
          {job.po_number && (
            <div className="col-span-2">
              <span className="text-slate-500">PO Number:</span>
              <span className="ml-2 font-bold text-emerald-600">{job.po_number}</span>
            </div>
          )}
        </div>

        <div className="bg-slate-50 p-3 rounded-lg">
          <p className="text-sm">{job.description}</p>
        </div>

        {/* Benchmark Info */}
        {job.benchmark && (
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-sm font-medium text-blue-800">Benchmark Rates</p>
            <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
              <div>
                <span className="text-blue-600">Min:</span> £{job.benchmark.min}
              </div>
              <div>
                <span className="text-blue-600">Avg:</span> £{job.benchmark.avg}
              </div>
              <div>
                <span className="text-blue-600">Max:</span> £{job.benchmark.max}
              </div>
            </div>
          </div>
        )}

        {/* Quotes Section */}
        <div>
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Quotes ({jobQuotes.length})
          </h4>
          
          {jobQuotes.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No quotes submitted yet</p>
          ) : (
            <div className="space-y-2">
              {jobQuotes.map(quote => (
                <div key={quote.id} className="border rounded-lg p-3 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{quote.contractor_name}</span>
                    <span className="text-lg font-bold">£{quote.total_cost.toFixed(2)}</span>
                  </div>
                  
                  <div className="text-sm text-slate-600 mb-2">
                    <span className="text-xs bg-slate-100 px-2 py-1 rounded">
                      Labour: £{quote.labour_cost} | Materials: £{quote.materials_cost} | 
                      Equipment: £{quote.equipment_cost} | Waste: £{quote.waste_cost}
                    </span>
                  </div>

                  <div className={`text-sm px-2 py-1 rounded inline-block mb-2 ${
                    quote.evaluation_result === 'APPROVE' ? 'bg-green-100 text-green-700' :
                    quote.evaluation_result === 'REVIEW' ? 'bg-amber-100 text-amber-700' :
                    quote.evaluation_result === 'REJECT' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {quote.evaluation_result}: {quote.evaluation_reason}
                  </div>

                  {quote.status === 'Pending' && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" onClick={() => approveQuote(quote.id)} className="bg-green-600 hover:bg-green-700">
                        <Check className="h-3 w-3 mr-1" /> Approve & Issue PO
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => rejectQuote(quote.id)} className="text-red-600">
                        <X className="h-3 w-3 mr-1" /> Reject
                      </Button>
                    </div>
                  )}

                  {quote.status === 'Accepted' && (
                    <Badge className="bg-green-100 text-green-700">Approved</Badge>
                  )}
                  {quote.status === 'Rejected' && (
                    <Badge className="bg-red-100 text-red-700">Rejected</Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Completion Status */}
        {job.status === 'Completed' || job.status === 'Awaiting Invoice' || job.status === 'Paid' ? (
          <div className="bg-green-50 p-3 rounded-lg">
            <p className="font-medium text-green-800 mb-2">Completion Status</p>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="flex items-center gap-1">
                {job.job_sheet_uploaded ? <Check className="h-4 w-4 text-green-600" /> : <X className="h-4 w-4 text-red-500" />}
                Job Sheet
              </div>
              <div className="flex items-center gap-1">
                {job.before_photos_uploaded ? <Check className="h-4 w-4 text-green-600" /> : <X className="h-4 w-4 text-red-500" />}
                Before Photos
              </div>
              <div className="flex items-center gap-1">
                {job.after_photos_uploaded ? <Check className="h-4 w-4 text-green-600" /> : <X className="h-4 w-4 text-red-500" />}
                After Photos
              </div>
            </div>
          </div>
        ) : null}

        {/* Invoice Status */}
        {job.invoice && (
          <div className={`p-3 rounded-lg ${job.invoice_status === 'Paid' ? 'bg-green-50' : 'bg-amber-50'}`}>
            <p className="font-medium mb-2">Invoice</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>Invoice: {job.invoice.invoice_number}</div>
              <div>Amount: £{job.invoice.amount?.toFixed(2)}</div>
              <div>Status: <Badge className={job.invoice_status === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{job.invoice_status}</Badge></div>
              {job.paid_at && <div>Paid: {new Date(job.paid_at).toLocaleDateString()}</div>}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#1F7A78] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading FM System...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1F7A78] to-[#0dbdad] text-white p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">LHS FM Operating System</h1>
              <p className="text-white/80 text-xs sm:text-sm">Facilities Management Dispatch, Quoting, Approval & Payment</p>
            </div>
            <div className="flex gap-2 sm:gap-3">
              <Button variant="secondary" onClick={downloadReport} className="flex-1 sm:flex-none text-xs sm:text-sm">
                <Download className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Payment Report</span>
              </Button>
              <Dialog open={showCreateJob} onOpenChange={setShowCreateJob}>
                <DialogTrigger asChild>
                  <Button className="bg-white text-[#1F7A78] hover:bg-white/90 flex-1 sm:flex-none text-xs sm:text-sm">
                    <Plus className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">New Job</span><span className="sm:hidden">New</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New FM Job</DialogTitle>
                  </DialogHeader>
                  <CreateJobForm />
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-3 sm:p-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <Card 
            className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'all' ? 'ring-2 ring-[#1F7A78]' : ''}`}
            onClick={() => handleKPIClick('all')}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-blue-50 rounded-lg">
                  <Briefcase className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{dashboard?.total_jobs || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'pending_quotes' ? 'ring-2 ring-[#1F7A78]' : ''}`}
            onClick={() => handleKPIClick('pending_quotes')}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-amber-50 rounded-lg">
                  <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{dashboard?.pending_quotes || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Quotes</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'awaiting_approval' ? 'ring-2 ring-[#1F7A78]' : ''}`}
            onClick={() => handleKPIClick('awaiting_approval')}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-orange-50 rounded-lg">
                  <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{dashboard?.awaiting_approval || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Approval</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'po_issued' ? 'ring-2 ring-[#1F7A78]' : ''}`}
            onClick={() => handleKPIClick('po_issued')}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-emerald-50 rounded-lg">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{dashboard?.status_counts?.['PO Issued'] || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">POs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'in_progress' ? 'ring-2 ring-[#1F7A78]' : ''}`}
            onClick={() => handleKPIClick('in_progress')}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-teal-50 rounded-lg">
                  <Truck className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{dashboard?.status_counts?.['In Progress'] || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'pending_invoices' ? 'ring-2 ring-[#1F7A78]' : ''}`}
            onClick={() => handleKPIClick('pending_invoices')}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-indigo-50 rounded-lg">
                  <Receipt className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{dashboard?.pending_invoices || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Invoices</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ⚠️ ATTENTION REQUIRED WIDGET */}
        {attentionJobs.length > 0 && (
          <Card className="border-0 shadow-sm mb-4 sm:mb-6 border-l-4 border-l-red-500 bg-red-50/50">
            <CardHeader className="pb-2 px-3 sm:px-6">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base sm:text-lg flex items-center gap-2 text-red-700">
                  <AlertOctagon className="h-5 w-5" />
                  Attention Required ({attentionJobs.length})
                </CardTitle>
                <Badge className="bg-red-100 text-red-700 animate-pulse">
                  Immediate Action
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pt-0">
              <div className="grid gap-2">
                {attentionJobs.slice(0, 5).map(job => {
                  const nextAction = getNextAction(job);
                  const isOverdue = job.sla_deadline && new Date(job.sla_deadline) < new Date();
                  return (
                    <div 
                      key={job.id || job.internal_id}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200 cursor-pointer hover:bg-red-50 transition-colors"
                      onClick={() => navigate(`/fm/jobs/${job.id || job.internal_id}`)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-[#1F7A78]">{job.job_reference}</span>
                          <Badge className={STATUS_COLORS[job.status] || 'bg-slate-100'}>
                            {job.status}
                          </Badge>
                          {isOverdue && (
                            <Badge className="bg-red-600 text-white text-xs">OVERDUE</Badge>
                          )}
                          {job.reassign_count > 0 && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">
                              Reassigned x{job.reassign_count}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          {job.site_name}{job.plot_number ? ` • Plot ${job.plot_number}` : ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-medium ${nextAction.color}`}>
                          {nextAction.text}
                        </p>
                        {job.sla_deadline && (
                          <p className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                            {isOverdue ? 'Was due: ' : 'Due: '}
                            {new Date(job.sla_deadline).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
                {attentionJobs.length > 5 && (
                  <Button 
                    variant="ghost" 
                    className="text-red-700 hover:bg-red-100"
                    onClick={() => setStatusFilter('escalated')}
                  >
                    View all {attentionJobs.length} jobs requiring attention
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Jobs List */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2 px-3 sm:px-6">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="text-base sm:text-lg">Job Pipeline</CardTitle>
                  {(statusFilter || categoryFilter) && (
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                      {categoryFilter && (
                        <Badge className="bg-amber-500 text-white text-xs">
                          {categoryFilter} ({filteredJobs.length})
                        </Badge>
                      )}
                      {statusFilter && (
                        <Badge className="bg-[#1F7A78] text-white text-xs">
                          {statusFilter.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} ({filteredJobs.length})
                        </Badge>
                      )}
                      <Button variant="ghost" size="sm" onClick={clearFilters} className="h-6 w-6 p-0">
                        <X className="h-3 w-3 sm:h-4 sm:w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                {/* Live Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input 
                    placeholder="Search by park, address, unit, task, contractor, job ID..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="fm-jobs-search-input"
                  />
                </div>
                
                <Tabs defaultValue="all" className="w-full">
                  <TabsList className="mb-4 flex w-full overflow-x-auto hide-scrollbar">
                    <TabsTrigger value="all" className="flex-1 text-xs sm:text-sm">All</TabsTrigger>
                    <TabsTrigger value="quotes" className="flex-1 text-xs sm:text-sm">Quotes</TabsTrigger>
                    <TabsTrigger value="active" className="flex-1 text-xs sm:text-sm">Active</TabsTrigger>
                    <TabsTrigger value="invoices" className="flex-1 text-xs sm:text-sm">Invoices</TabsTrigger>
                  </TabsList>

                  <TabsContent value="all">
                    <ScrollArea className="h-[400px] sm:h-[500px]">
                      <div className="space-y-2">
                        {filteredJobs.length === 0 ? (
                          <div className="text-center text-slate-500 py-8 text-sm">
                            No jobs found for this filter
                          </div>
                        ) : filteredJobs.map(job => {
                          const nextAction = getNextAction(job);
                          return (
                            <div
                              key={job.id}
                              className={`p-2.5 sm:p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors ${
                                selectedJob?.id === job.id ? 'border-[#1F7A78] bg-slate-50' : ''
                              }`}
                              onClick={() => setSelectedJob(job)}
                              onDoubleClick={() => navigate(`/fm/jobs/${job.id}`)}
                              data-testid={`fm-job-card-${job.id}`}
                              title="Click to preview, double-click to open full details"
                            >
                              {/* Row 1: Job ID, Stage, Next Action */}
                              <div className="flex items-center justify-between mb-1">
                                <span 
                                  className="font-medium text-sm sm:text-base text-[#1F7A78] hover:underline"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/fm/jobs/${job.id}`); }}
                                >
                                  {job.job_reference}
                                </span>
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs font-medium ${nextAction.color}`}>{nextAction.text}</span>
                                  <Badge className={`text-[10px] sm:text-xs ${STATUS_COLORS[job.status] || 'bg-slate-100'}`}>{job.status}</Badge>
                                </div>
                              </div>
                              {/* Row 2: Park / Asset / Postcode */}
                              <div className="text-xs sm:text-sm text-slate-600 truncate">
                                {job.site_name}{job.plot_number ? ` • Plot ${job.plot_number}` : ''}{job.postcode ? ` • ${job.postcode}` : ''}
                              </div>
                              {/* Row 3: Job Type, Priority, Contractor, Due Date, Quote Value */}
                              <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-1 text-[10px] sm:text-xs">
                                <span className="bg-slate-100 px-1.5 sm:px-2 py-0.5 rounded truncate max-w-[100px]">{job.job_type_label}</span>
                                <Badge className={`text-[10px] sm:text-xs ${PRIORITY_COLORS[job.priority]}`}>{job.priority}</Badge>
                                {job.assigned_contractor_name && (
                                  <span className="text-slate-600 truncate max-w-[100px]" title={job.assigned_contractor_name}>
                                    {job.assigned_contractor_name.split(' ')[0]}
                                  </span>
                                )}
                                {job.sla_deadline && (
                                  <span className="text-slate-500">
                                    Due: {new Date(job.sla_deadline).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                  </span>
                                )}
                                {(job.approved_quote?.total_value || job.approved_quote?.total || job.approved_quote?.total_cost) && (
                                  <span className="text-emerald-600 font-medium">
                                    £{(job.approved_quote?.total_value || job.approved_quote?.total || job.approved_quote?.total_cost || 0).toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  <TabsContent value="quotes">
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-2">
                        {jobs.filter(j => ['Awaiting Quote', 'Quote Awaiting Approval'].includes(j.status)).length === 0 ? (
                          <div className="text-center text-slate-500 py-8 text-sm">No jobs in quote stage</div>
                        ) : jobs.filter(j => ['Awaiting Quote', 'Quote Awaiting Approval'].includes(j.status)).map(job => {
                          const nextAction = getNextAction(job);
                          return (
                            <div
                              key={job.id}
                              className={`p-3 border rounded-lg cursor-pointer hover:bg-slate-50 ${
                                selectedJob?.id === job.id ? 'border-[#1F7A78] bg-slate-50' : ''
                              }`}
                              onClick={() => setSelectedJob(job)}
                              onDoubleClick={() => navigate(`/fm/jobs/${job.id}`)}
                              title="Click to preview, double-click to open full details"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span 
                                  className="font-medium text-[#1F7A78] hover:underline"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/fm/jobs/${job.id}`); }}
                                >
                                  {job.job_reference}
                                </span>
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs font-medium ${nextAction.color}`}>{nextAction.text}</span>
                                  <Badge className={STATUS_COLORS[job.status]}>{job.status}</Badge>
                                </div>
                              </div>
                              <div className="text-sm text-slate-600">{job.site_name} - {job.job_type_label}</div>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  <TabsContent value="active">
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-2">
                        {jobs.filter(j => ['Approved', 'Assigned', 'Scheduled'].includes(j.status)).length === 0 ? (
                          <div className="text-center text-slate-500 py-8 text-sm">No active jobs</div>
                        ) : jobs.filter(j => ['Approved', 'Assigned', 'Scheduled'].includes(j.status)).map(job => {
                          const nextAction = getNextAction(job);
                          return (
                            <div
                              key={job.id}
                              className={`p-3 border rounded-lg cursor-pointer hover:bg-slate-50 ${
                                selectedJob?.id === job.id ? 'border-[#1F7A78] bg-slate-50' : ''
                              }`}
                              onClick={() => setSelectedJob(job)}
                              onDoubleClick={() => navigate(`/fm/jobs/${job.id}`)}
                              title="Click to preview, double-click to open full details"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span 
                                  className="font-medium text-[#1F7A78] hover:underline"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/fm/jobs/${job.id}`); }}
                                >
                                  {job.job_reference}
                                </span>
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs font-medium ${nextAction.color}`}>{nextAction.text}</span>
                                  <Badge className={STATUS_COLORS[job.status]}>{job.status}</Badge>
                                </div>
                              </div>
                              <div className="text-sm text-slate-600">{job.site_name}</div>
                              {job.assigned_contractor_name && (
                                <div className="text-xs text-cyan-600 mt-1">Contractor: {job.assigned_contractor_name}</div>
                              )}
                              {job.po_number && (
                                <div className="text-xs text-emerald-600 font-medium mt-1">PO: {job.po_number}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  <TabsContent value="invoices">
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-2">
                        {jobs.filter(j => ['Completed', 'Closed'].includes(j.status)).length === 0 ? (
                          <div className="text-center text-slate-500 py-8 text-sm">No jobs in invoice stage</div>
                        ) : jobs.filter(j => ['Completed', 'Closed'].includes(j.status)).map(job => {
                          const nextAction = getNextAction(job);
                          return (
                            <div
                              key={job.id}
                              className={`p-3 border rounded-lg cursor-pointer hover:bg-slate-50 ${
                                selectedJob?.id === job.id ? 'border-[#1F7A78] bg-slate-50' : ''
                              }`}
                              onClick={() => setSelectedJob(job)}
                              onDoubleClick={() => navigate(`/fm/jobs/${job.id}`)}
                              title="Click to preview, double-click to open full details"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span 
                                  className="font-medium text-[#1F7A78] hover:underline"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/fm/jobs/${job.id}`); }}
                                >
                                  {job.job_reference}
                                </span>
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs font-medium ${nextAction.color}`}>{nextAction.text}</span>
                                  <Badge className={STATUS_COLORS[job.status]}>{job.status}</Badge>
                                </div>
                              </div>
                              <div className="text-sm text-slate-600">{job.site_name}</div>
                              {job.invoice && (
                                <div className="text-sm mt-1">
                                  Invoice: £{job.invoice.amount?.toFixed(2)} - {job.invoice_status}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Job Detail Panel */}
          <div>
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Job Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedJob ? (
                  <JobDetailPanel job={selectedJob} />
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <ClipboardCheck className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Select a job to view details</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
